import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Pause, Maximize, Truck, CreditCard, Lock, Volume2, VolumeX } from 'lucide-react';
import { trackBannerClick } from '@/utils/analytics';

// Vídeos do carrossel - carregados sob demanda (lazy loading)
const CAROUSEL_VIDEOS = [
  { video: "/videos/video_1.mp4", poster: "/products/img_1_new.jpg" },
  { video: "/videos/banner_video_2.mp4", poster: "/products/poster_banner_2.jpg" },
  { video: "/videos/video_7.mp4", poster: "/products/poster_video_7.jpg" },
  { video: "/videos/video_8.mp4", poster: "/products/poster_video_8.jpg" },
  { video: "/videos/banner_cargo_1.mp4", poster: "/products/poster_cargo_1.jpg" },
  { video: "/videos/banner_cargo_2.mp4", poster: "/products/poster_cargo_2.jpg" },
  { video: "/videos/banner_cargo_3.mp4", poster: "/products/poster_cargo_3.jpg" },
  { video: "/videos/video_2.mp4", poster: "/products/img_2_new.jpg" },
  { video: "/videos/video_3.mp4", poster: "/products/img_3_new.jpg" },
  { video: "/videos/video_4.mp4", poster: "/products/img_4_new.jpg" },
  { video: "/videos/video_5.mp4", poster: "/products/img_5_new_3.jpg" },
];

export default function HeroBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [slideDirection, setSlideDirection] = useState('none');
  const [isAnimating, setIsAnimating] = useState(false);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Pausar vídeo no segundo 4.7 APENAS no primeiro vídeo
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (currentSlide === 0) {
        // Primeiro vídeo: começa no 3.7 e pausa quando chegar em 4.7
        const handleTimeUpdate = () => {
          if (video.currentTime >= 4.7) {
            video.pause();
            setIsPlaying(false);
            video.removeEventListener('timeupdate', handleTimeUpdate);
          }
        };
        
        const initVideo = async () => {
          try {
            video.currentTime = 3.7;
            await video.play();
            setIsPlaying(true);
            video.addEventListener('timeupdate', handleTimeUpdate);
          } catch (e) {
            video.currentTime = 4.7;
          }
        };
        
        video.addEventListener('loadedmetadata', initVideo);
        return () => {
          video.removeEventListener('loadedmetadata', initVideo);
          video.removeEventListener('timeupdate', handleTimeUpdate);
        };
      } else {
        // Outros vídeos: rodam normalmente
        setIsPlaying(true);
      }
    }
  }, [currentSlide]);

  // Função para trocar slide com animação
  const changeSlide = (newIndex, direction) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection(direction);
    
    // Após a saída, troca o vídeo e anima a entrada
    setTimeout(() => {
      setCurrentSlide(newIndex);
      setIsPlaying(true);
      if (newIndex !== 0) setIsMuted(false);
      setSlideDirection(direction === 'left' ? 'enter-left' : 'enter-right');
      
      setTimeout(() => {
        setSlideDirection('none');
        setIsAnimating(false);
      }, 350);
    }, 300);
  };

  const goToSlide = (index) => {
    if (index === currentSlide || isAnimating) return;
    changeSlide(index, index > currentSlide ? 'left' : 'right');
  };

  const nextSlide = () => {
    const next = (currentSlide + 1) % CAROUSEL_VIDEOS.length;
    changeSlide(next, 'left');
  };

  const prevSlide = () => {
    const prev = (currentSlide - 1 + CAROUSEL_VIDEOS.length) % CAROUSEL_VIDEOS.length;
    changeSlide(prev, 'right');
  };

  // Controles do vídeo
  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoClick = () => {
    setShowControls(!showControls);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  return (
    <section 
      className="relative overflow-hidden bg-[#0d0d0d]"
      data-testid="hero-section"
    >
      
      {/* Conteúdo principal */}
      <div className="relative z-10 pt-14 md:pt-20 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 w-full">
          {/* Layout: Texto à esquerda, Vídeo à direita - mesmo em mobile */}
          <div className="flex flex-row items-start justify-between gap-4 md:gap-12">
            
            {/* Texto e CTA - Esquerda */}
            <div className="flex-1 text-left pt-4 md:pt-8">
              {/* Título principal - Playfair Display italic, elegante e luxuoso */}
              <h1 
                className="text-[28px] md:text-[56px] lg:text-[72px] font-semibold text-[#F5F5F0] leading-[1.05] tracking-wide mb-3 md:mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Estilo e Conforto!
              </h1>
              
              {/* Subtítulo - Cormorant Garamond, tom vermelho premium */}
              <p 
                className="text-[22px] md:text-[44px] lg:text-[56px] font-semibold text-[#C41E3A] leading-[1.1] tracking-wide mb-6 md:mb-12"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Direto da Fábrica
              </p>

              {/* Badges com check */}
              <div className="flex flex-col gap-5 md:gap-6 items-start">
                <div className="flex items-center gap-2 md:gap-3 text-[#F5F5F0]">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-[#C41E3A]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[13px] md:text-[18px] tracking-[0.05em] font-light uppercase">Lider em vendas</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-[#F5F5F0]">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-[#C41E3A]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[13px] md:text-[18px] tracking-[0.05em] font-light uppercase">Tecido Premium</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-[#F5F5F0]">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-[#C41E3A]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[13px] md:text-[18px] tracking-[0.05em] font-light uppercase">Envio em até 24h</span>
                </div>
              </div>

              {/* Botão Comprar Agora - Montserrat */}
              <a 
                href="/produtos"
                onClick={() => trackBannerClick('comprar_agora')}
                className="relative overflow-hidden mt-8 md:mt-14 inline-flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#a01830] text-white font-semibold px-4 py-2 md:px-6 md:py-2.5 text-xs md:text-sm tracking-[0.05em] transition-all duration-300 rounded-full group"
                style={{ fontFamily: "var(--font-body)" }}
                data-testid="cta-compra-agora"
              >
                <div 
                  className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-full"
                >
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.1) 70%, transparent 100%)',
                      animation: 'shimmer-slide 2.8s ease-in-out infinite',
                    }}
                  />
                </div>
                Comprar Agora
                <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
            </div>

            {/* Vídeo - Direita */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className={`relative transition-all duration-300 ${
                isExpanded 
                  ? 'w-[213px] h-[355px] md:w-[403px] md:h-[671px] lg:w-[474px] lg:h-[789px]' 
                  : 'w-[178px] h-[297px] md:w-[332px] md:h-[552px] lg:w-[379px] lg:h-[631px]'
              }`}>
                {/* Vídeo sem moldura */}
                <div 
                  ref={videoContainerRef}
                  className="relative w-full h-full rounded-xl overflow-hidden bg-[#0d0d0d] cursor-pointer group"
                  style={{ boxShadow: 'none' }}
                  onClick={handleVideoClick}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <video
                    ref={videoRef}
                    key={currentSlide}
                    className={`w-full h-full object-cover ${
                      !isPlaying 
                        ? 'saturate-[1.15] contrast-[1.08] brightness-[1.02]' 
                        : ''
                    }`}
                    style={{
                      transition: 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.35s ease',
                      transform: slideDirection === 'left' ? 'translateX(-30%) scale(0.95)' 
                               : slideDirection === 'right' ? 'translateX(30%) scale(0.95)'
                               : slideDirection === 'enter-left' ? 'translateX(0) scale(1)'
                               : slideDirection === 'enter-right' ? 'translateX(0) scale(1)'
                               : 'translateX(0) scale(1)',
                      opacity: slideDirection === 'left' || slideDirection === 'right' ? 0 : 1,
                    }}
                    muted={isMuted}
                    playsInline
                    preload="auto"
                    autoPlay
                    loop
                  >
                    <source src={CAROUSEL_VIDEOS[currentSlide].video} type="video/mp4" />
                  </video>
                  
                  {/* Controles - aparecem no hover/toque */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={togglePlay}
                        className="text-white/80 hover:text-white transition-colors"
                        aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
                      >
                        {isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      
                      <div className="flex-1"></div>

                      <button
                        onClick={toggleMute}
                        className="text-white/80 hover:text-white transition-colors mr-2"
                        aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
                      >
                        {isMuted ? (
                          <VolumeX className="h-4 w-4" />
                        ) : (
                          <Volume2 className="h-4 w-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={toggleExpand}
                        className="text-white/80 hover:text-white transition-colors"
                        aria-label="Expandir"
                      >
                        <Maximize className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Setas de navegação - compacto e minimalista */}
              <div className="flex items-center justify-center gap-1 mt-3">
                <button 
                  onClick={prevSlide}
                  className="hover:opacity-100 opacity-50 transition-all p-2"
                  aria-label="Vídeo anterior"
                >
                  <ChevronLeft className="h-4 w-4 text-white/70" />
                </button>
                
                <button 
                  onClick={nextSlide}
                  className="hover:opacity-100 opacity-50 transition-all p-2"
                  aria-label="Próximo vídeo"
                >
                  <ChevronRight className="h-4 w-4 text-white/70" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info badges na borda inferior do banner */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/40 backdrop-blur-sm py-2.5 md:py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-center items-center gap-5 md:gap-12 text-white/80" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <div className="flex items-center gap-1.5">
              <Truck className="h-2.5 w-2.5 md:h-3.5 md:w-3.5" />
              <span className="text-[8px] md:text-[11px] font-medium tracking-[0.05em]">Entrega em todo Brasil</span>
            </div>
            <span className="text-white/30">|</span>
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-2.5 w-2.5 md:h-3.5 md:w-3.5" />
              <span className="text-[8px] md:text-[11px] font-medium tracking-[0.05em]">Até 12x</span>
            </div>
            <span className="text-white/30">|</span>
            <Link 
              to="/sobre" 
              className="flex items-center gap-1 text-white/80 hover:text-white transition-colors cursor-pointer group"
              data-testid="site-seguro-link"
            >
              <Lock className="h-2.5 w-2.5 md:h-3.5 md:w-3.5" />
              <span className="text-[8px] md:text-[11px] font-medium tracking-[0.05em] underline decoration-[#C41E3A] underline-offset-2">Site Seguro</span>
              <ChevronRight className="h-2 w-2 md:h-3 md:w-3 text-[#C41E3A] group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
